import React, { useState } from 'react';
import { Form, Modal } from 'antd';
import { uniqWith } from 'lodash';

import { clearFieldErrors, handleBulkRecruitmentsUpdateErrors } from '../../common/errorHelpers';
import { RecruiterType, StatusType } from '../../common/types';
import { useGetProject } from '../../common/useGetProject';
import { useGetRecruitmentStatusDictionary } from '../../common/useGetRecruitmentStatusDictionary';
import { FormCheckbox } from '../../components/forms/FormCheckbox';
import { FormFieldLabel } from '../../components/forms/FormFieldLabel';
import { FormSelect } from '../../components/forms/FormSelect';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { CandidateType } from '../candidates/Candidates.types';
import { SaveButtonIcon } from '../dictionaries/DictionaryItemsList';
import { ProjectType } from '../projects/ProjectsTable.types';
import { RecruitmentHistoryType } from '../recruitments/Recruitments.types';

import { PropagateTable } from './PropagateTable';
import { ClearButton, SubmitButton } from './RecruitmentDataForm';
import { useRecruitmentsBulkUpdate } from './useRecruitmentsBulkUpdate';

export const responsibleOptions = (currentStatus: StatusType, projectData?: ProjectType) => {
  if (!currentStatus) return [];

  const mapUserKeys = {
    RECRUITER: 'recruiters' as keyof ProjectType,
    COORDINATOR: 'coordinators' as keyof ProjectType,
    BACKOFFICE: 'backoffices' as keyof ProjectType,
    INTERVIEWER: 'interviewers' as keyof ProjectType,
  };

  const projectRoleForStatusName = mapUserKeys[currentStatus?.responsibility as keyof typeof mapUserKeys];

  return projectData
    ? (projectData[projectRoleForStatusName] as RecruiterType[]).map((user) => ({
        value: user.id,
        content: `${user.firstName} ${user.lastName}`,
      }))
    : [];
};

type StatusAndNoteFormProps = {
  recruitment: RecruitmentHistoryType;
  candidate: CandidateType;
};

export const StatusAndNoteForm: React.FC<StatusAndNoteFormProps> = ({ candidate, recruitment }) => {
  const [form] = Form.useForm();
  const recruitmentsBulkUpdate = useRecruitmentsBulkUpdate();
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);

  const { data: statusesData, isLoading: statusesLoading } = useGetRecruitmentStatusDictionary();
  const { data: projectData } = useGetProject(recruitment.projectUuid);

  const currentStatusName = Form.useWatch('status', form);
  const currentStatus = statusesData?.find((status) => status.id === currentStatusName);

  const statusesOptions =
    !statusesLoading && statusesData
      ? statusesData.map((status) => ({
          value: status.id,
          content: status.name,
        }))
      : [];

  const initialValues = {
    status: recruitment.status?.id,
    responsible: recruitment.responsible?.id,
    perfectMatch: recruitment.perfectMatch,
  };

  const setFormPropagate = (propagate: React.Key[]) => {
    form.setFieldValue('propagate', propagate);
  };

  const submitForm = () => {
    const { note, responsible, status, propagate, perfectMatch } = form.getFieldsValue();

    const payload = {
      ...({ note } ?? []),
      perfectMatch,
      status,
      responsible,
    };

    const recruitmentsToUpdate = [recruitment.uuid, ...(propagate?.length ? propagate : [])];

    recruitmentsBulkUpdate.mutate(
      {
        data: { ...payload, recruitments: recruitmentsToUpdate },
      },
      {
        onSuccess: () => {
          form.setFieldValue('note', null);
          clearFieldErrors(form);
        },
        onError: (error) => {
          handleBulkRecruitmentsUpdateErrors(error, form, candidate.recruitments);
        },
      }
    );
  };

  const handleSubmit = () => {
    const { status } = form.getFieldsValue();
    const selectedStatus = statusesData?.find((s) => s.id === status);
    const shouldShowConsentModal = selectedStatus?.name === '14 CV sent to client';

    if (shouldShowConsentModal) {
      setIsConsentModalOpen(true);
      return;
    }

    submitForm();
  };

  return (
    <Form form={form} initialValues={initialValues} layout="vertical" colon={false}>
      <StyledColumn gap="0.3rem">
        <FormSelect
          name="status"
          label="Status"
          allowClear={false}
          options={statusesOptions}
          onChange={() => form.setFieldValue('responsible', '')}
        />

        <FormCheckbox name="perfectMatch" label="Perfect match" />

        {currentStatus?.responsibility && (
          <FormSelect
            name="responsible"
            label="Responsible"
            allowClear={false}
            options={uniqWith(
              [
                ...responsibleOptions(currentStatus, projectData),
                ...(recruitment.responsible && form.getFieldValue('status') === initialValues.status
                  ? [
                      {
                        value: recruitment.responsible?.id,
                        content: `${recruitment.responsible?.firstName} ${recruitment.responsible?.lastName}`,
                      },
                    ]
                  : []),
              ],
              (responsible1, responsible2) => responsible1.value === responsible2.value
            )}
          />
        )}

        <FormTextArea name="note" label="Note" rows={9} />

        <FormFieldLabel>Propagate changes to other projects</FormFieldLabel>

        <Form.Item name="propagate">
          <PropagateTable
            recruitments={candidate.recruitments.filter((element) => element.uuid !== recruitment.uuid)}
            chooseRecruitment={setFormPropagate}
          />
        </Form.Item>

        <StyledRow justifyContent="flex-end" marginTop="14px">
          <ClearButton onClick={() => form.resetFields()}>Clear</ClearButton>
          <SubmitButton onClick={handleSubmit} icon={<SaveButtonIcon />}>
            Save
          </SubmitButton>
        </StyledRow>

        <Modal
          open={isConsentModalOpen}
          title="Client Data Processing consent required"
          okText="Confirm & save"
          cancelText="Cancel"
          onOk={() => {
            setIsConsentModalOpen(false);
            submitForm();
          }}
          onCancel={() => setIsConsentModalOpen(false)}
        >
          <>
            <p>
              Before changing the recruitment status to &quot;
              {statusesData?.find((s) => s.id === form.getFieldValue('status'))?.name}
              &quot;, confirm that the candidate has accepted the Client Data Processing consent.
            </p>
            <p>
              You can verify this on the{' '}
              <a href={`/candidates/${candidate.uuid}`} target="_blank" rel="noopener noreferrer">
                candidate details page
              </a>
              .
            </p>
          </>
        </Modal>
      </StyledColumn>
    </Form>
  );
};
